import React from 'react';
import { motion } from 'framer-motion';
import { Sparkles, Shield, Clock, Award } from 'lucide-react';
import { Helmet } from 'react-helmet-async';

import Hero from '../components/Hero';
import Services from '../components/Services';
import Packages from '../components/Packages';
import Gallery from '../components/Gallery';
import Reviews from '../components/Reviews';

const Home = ({ lang = 'en' }) => {
  const isAr = lang === 'ar';

  const content = {
    seo: {
      title: isAr
        ? 'أفضل غسيل سيارات متنقل في الكويت | سوبر كلاسيكي'
        : 'Best Mobile Car Wash in Kuwait | Super Classy',
      desc: isAr
        ? 'تقدم سوبر كلاسيكي خدمات غسيل وتلميع السيارات المتنقلة الاحترافية في الكويت. تنظيف داخلي وخارجي وتلميع شامل عند باب بيتك.'
        : 'Super Classy offers professional mobile car wash and detailing services in Kuwait. Interior, exterior, and full detailing at your doorstep.',
    },
    h1: isAr
      ? 'أفضل خدمة غسيل سيارات متنقل في الكويت'
      : 'Best Mobile Car Wash Service in Kuwait',
    subtitle: isAr
      ? 'استمتع بالعناية المتميزة بالسيارات مع خدمتنا الاحترافية. نأتي إليك أينما كنت لنقدم لك نتائج بجودة الوكالة.'
      : 'Experience premium car care with our professional mobile car wash service. We come to your location and deliver showroom-quality results.',
    features: [
      {
        icon: <Sparkles className='h-8 w-8 text-white' />,
        title: isAr ? 'تلميع سيارات بريميوم' : 'Premium Car Detailing',
        description: isAr
          ? 'منتجات عالية الجودة وتقنيات تلميع متطورة'
          : 'Professional-grade products and advanced detailing techniques',
      },
      {
        icon: <Shield className='h-8 w-8 text-white' />,
        title: isAr ? 'غسيل صديق للبيئة' : 'Eco-Friendly Car Wash',
        description: isAr
          ? 'تقنية توفير المياه ومنتجات تنظيف قابلة للتحلل'
          : 'Water-saving technology and biodegradable cleaning products',
      },
      {
        icon: <Clock className='h-8 w-8 text-white' />,
        title: isAr ? 'خدمة متنقلة سريعة' : 'Fast Mobile Service',
        description: isAr
          ? 'متوسط ٤٥-٦٠ دقيقة لكل مركبة في موقعك'
          : 'Average 45–60 minutes per vehicle at your location',
      },
      {
        icon: <Award className='h-8 w-8 text-white' />,
        title: isAr ? 'خبراء غسيل معتمدون' : 'Certified Detailing Experts',
        description: isAr
          ? 'محترفون مدربون ذوو خبرة سنوات في الكويت'
          : 'Trained professionals with years of experience in Kuwait',
      },
    ],
  };

  return (
    <>
      {/* Dynamic SEO Meta Tags */}
      <Helmet>
        <title>{content.seo.title}</title>
        <meta name='description' content={content.seo.desc} />
        <meta
          name='keywords'
          content='superclassykw, غسيل سيارات متنقل الكويت, تلميع سيارات, نانو سيراميك الكويت, Car Wash Kuwait, Mobile Detailing Kuwait'
        />
        <link rel='canonical' href='https://superclassykw.com/' />
      </Helmet>

      <div className='overflow-hidden' dir={isAr ? 'rtl' : 'ltr'}>
        <Hero lang={lang} />

        {/* Features Section */}
        <section className='section-padding py-20 bg-black dark:bg-gray-900'>
          <div className='max-w-7xl mx-auto'>
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className='text-center mb-16'
            >
              <h1 className='text-4xl md:text-6xl font-black mb-6 text-gradient leading-tight'>
                {content.h1}
              </h1>

              <p className='text-xl text-gray-600 dark:text-gray-400 max-w-3xl mx-auto leading-relaxed'>
                {content.subtitle}
              </p>
            </motion.div>

            <div className='grid md:grid-cols-2 lg:grid-cols-4 gap-8'>
              {content.features.map((feature, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, y: 50 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: index * 0.1 }}
                  className='glass-card p-8 text-center group hover:scale-105 transition-all duration-300 border border-white/10 shadow-xl'
                >
                  <div className='inline-flex p-4 premium-gradient rounded-2xl mb-6 group-hover:rotate-6 transition-transform shadow-lg shadow-royal-purple-500/20'>
                    {feature.icon}
                  </div>
                  <h2 className='text-2xl font-black mb-3'>{feature.title}</h2>
                  <p className='text-gray-600 dark:text-gray-400 font-medium leading-relaxed'>
                    {feature.description}
                  </p>
                </motion.div>
              ))}
            </div>
          </div>
        </section>

        {/* Passing state down to all major sections */}
        <Services lang={lang} />
        <Packages lang={lang} />
        <Gallery lang={lang} />
        <Reviews lang={lang} />
      </div>
    </>
  );
};

export default Home;
